<template>
  <div class="sip-trunk-page">
    <div class="content-area">
      <div class="page-header">
        <h1 class="page-title">
          SIP &amp; dispatch
        </h1>
        <p class="page-subtitle">
          Configure your inbound SIP trunk and LiveKit dispatch rules.
        </p>
      </div>

      <div class="main-content">
        <div v-if="loadingInitial" class="loading-state">
          <v-progress-circular indeterminate color="primary" size="48"/>
          <p class="loading-text">
            Loading SIP configuration…
          </p>
        </div>

        <template v-else>
          <v-row class="section-row sip-section-row">
            <v-col cols="12" md="6">
              <v-card class="section-card sip-panel-card" outlined>
                <div class="sip-panel-header">
                  <h2 class="sip-panel-title">
                    Inbound SIP trunk
                  </h2>
                  <p class="sip-panel-description text-body-2 text--secondary">
                    Provision LiveKit inbound SIP trunk credentials, numbers, and optional source filters.
                  </p>
                </div>
                <v-card-text class="sip-panel-body">
                  <v-alert
                    v-if="hasTrunks"
                    type="info"
                    dense
                    outlined
                    class="sip-banner mb-4">
                    A trunk exists. Use the row pencil to edit, or trash to delete and replace.
                  </v-alert>

                  <div v-if="trunks.length === 0" class="sip-empty-copy mb-4">
                    <span class="text-body-3 text--secondary">
                      No inbound trunk yet. Complete the form below.
                    </span>
                  </div>

                  <div v-if="trunks.length" class="sip-table-wrap mb-2">
                    <v-data-table
                      :headers="trunkTableHeaders"
                      :items="trunkTableRows"
                      item-key="key"
                      dense
                      class="sip-data-table"
                      hide-default-footer
                      :items-per-page="-1"
                      no-data-text="Nothing to show">
                      <template #item.actions="{ item }">
                        <div class="sip-row-actions sip-row-actions--split">
                          <v-btn
                            v-if="item.deleteTrunkId"
                            icon
                            small
                            class="mank-btn-icon"
                            :disabled="deleteLoading"
                            @click="openDetailDrawer('trunk', item.deleteTrunkId)"
                            aria-label="View and edit trunk">
                            <Icon name="edit" variant="solid" size="16" color="default"/>
                          </v-btn>
                          <v-btn
                            v-if="item.deleteTrunkId"
                            icon
                            small
                            class="mank-btn-icon"
                            :disabled="deleteLoading"
                            @click="openDeleteTrunk(item)"
                            aria-label="Delete trunk">
                            <Icon name="trash" variant="solid" size="16" color="default"/>
                          </v-btn>
                        </div>
                      </template>
                    </v-data-table>
                  </div>

                  <template v-if="!hasTrunks">
                    <v-divider class="sip-divider"/>

                    <div class="sip-form-block">
                      <h3 class="ds-panel-title sip-form-heading">
                        New inbound trunk
                      </h3>
                      <p class="sip-form-intro text-body-3 text--secondary">
                        Credentials and numbers from your carrier or PBX.
                      </p>

                      <v-row dense class="sip-form-grid">
                        <v-col cols="12" sm="6">
                          <div class="form-group">
                            <label class="input-label">
                              Trunk name <span class="required">*</span>
                            </label>
                            <MankTextField
                              v-model="trunkForm.name"
                              dense
                              hide-details="auto"
                              placeholder="e.g. main-pbx"/>
                          </div>
                        </v-col>
                        <v-col cols="12" sm="6">
                          <div class="form-group">
                            <label class="input-label">
                              SIP username <span class="required">*</span>
                            </label>
                            <MankTextField
                              v-model="trunkForm.auth_username"
                              dense
                              hide-details="auto"
                              autocomplete="username"
                              placeholder="SIP username"/>
                          </div>
                        </v-col>
                        <v-col cols="12">
                          <div class="form-group">
                            <label class="input-label">
                              SIP password <span class="required">*</span>
                            </label>
                            <MankTextField
                              v-model="trunkForm.auth_password"
                              type="password"
                              dense
                              hide-details="auto"
                              autocomplete="new-password"
                              placeholder="••••••••"/>
                          </div>
                        </v-col>
                        <v-col cols="12">
                          <div class="form-group">
                            <label class="input-label">
                              Phone numbers
                            </label>
                            <MankTextarea
                              v-model="trunkForm.numbersRaw"
                              rows="2"
                              dense
                              hide-details="auto"
                              placeholder="Comma or one per line (+1… )"/>
                          </div>
                        </v-col>
                        <v-col cols="12">
                          <div class="form-group">
                            <label class="input-label">
                              Allowed source addresses (optional)
                            </label>
                            <MankTextarea
                              v-model="trunkForm.allowedAddressesRaw"
                              rows="2"
                              dense
                              hide-details="auto"
                              placeholder="IPs or CIDR ranges"/>
                          </div>
                        </v-col>
                        <v-col cols="12">
                          <div class="form-group">
                            <label class="input-label">
                              Metadata (optional)
                            </label>
                            <MankTextField
                              v-model="trunkForm.metadata"
                              dense
                              hide-details="auto"
                              placeholder="Internal reference"/>
                          </div>
                        </v-col>
                      </v-row>

                      <div class="form-actions">
                        <v-btn
                          color="primary"
                          class="mank-btn"
                          elevation="0"
                          :loading="savingTrunk"
                          :disabled="!canSubmitTrunk || savingTrunk"
                          @click="submitTrunk">
                          <Icon name="plus" variant="solid" size="20" color="onPrimary"/>
                          Create trunk
                        </v-btn>
                      </div>
                    </div>
                  </template>
                </v-card-text>
              </v-card>
            </v-col>

            <v-col cols="12" md="6">
              <v-card class="section-card sip-panel-card" outlined>
                <div class="sip-panel-header">
                  <h2 class="sip-panel-title">
                    Dispatch rules
                  </h2>
                  <p class="sip-panel-description text-body-2 text--secondary">
                    Map inbound calls to a LiveKit room and attach an agent via room configuration.
                  </p>
                </div>
                <v-card-text class="sip-panel-body">
                  <v-alert
                    v-if="hasDispatchRules"
                    type="info"
                    dense
                    outlined
                    class="sip-banner mb-4">
                    Use the row pencil to view and edit dispatch, or trash to delete the rule before creating another.
                  </v-alert>

                  <v-alert
                    v-else-if="!hasTrunks"
                    type="warning"
                    dense
                    outlined
                    class="sip-banner mb-4">
                    Create an inbound trunk first. Dispatch rules need a trunk id.
                  </v-alert>

                  <div v-if="dispatchRules.length === 0" class="sip-empty-copy mb-4">
                    <span class="text-body-3 text--secondary">
                      No dispatch rules yet.
                    </span>
                  </div>

                  <div v-else class="sip-table-wrap mb-2">
                    <v-data-table
                      :headers="dispatchTableHeaders"
                      :items="dispatchTableRows"
                      item-key="key"
                      dense
                      class="sip-data-table"
                      hide-default-footer
                      :items-per-page="-1"
                      no-data-text="Nothing to show">
                      <template #item.actions="{ item }">
                        <div class="sip-row-actions sip-row-actions--split">
                          <v-btn
                            v-if="item.deleteRuleId"
                            icon
                            small
                            class="mank-btn-icon"
                            :disabled="deleteLoading"
                            @click="openDetailDrawer('dispatch', item.deleteRuleId)"
                            aria-label="View and edit dispatch rule">
                            <Icon name="edit" variant="solid" size="16" color="default"/>
                          </v-btn>
                          <v-btn
                            v-if="item.deleteRuleId"
                            icon
                            small
                            class="mank-btn-icon"
                            :disabled="deleteLoading"
                            @click="openDeleteDispatch(item)">
                            <Icon name="trash" variant="solid" size="16" color="default"/>
                          </v-btn>
                        </div>
                      </template>
                    </v-data-table>
                  </div>

                  <template v-if="canCreateDispatch">
                    <v-divider class="sip-divider"/>

                    <div class="sip-form-block">
                      <h3 class="ds-panel-title sip-form-heading">
                        New dispatch rule
                      </h3>
                      <p class="sip-form-intro text-body-3 text--secondary">
                        Direct rule: calls land in the room below; the agent joins from room config.
                      </p>

                      <div class="form-group">
                        <label class="input-label">
                          Rule name (optional)
                        </label>
                        <MankTextField
                          v-model="dispatchForm.ruleName"
                          dense
                          hide-details="auto"
                          placeholder="e.g. default-inbound"/>
                      </div>
                      <div class="form-group">
                        <label class="input-label">
                          Room name <span class="required">*</span>
                        </label>
                        <MankTextField
                          v-model="dispatchForm.roomName"
                          dense
                          hide-details="auto"
                          placeholder="LiveKit room name"/>
                      </div>
                      <div class="form-group">
                        <label class="input-label">
                          Agent name
                        </label>
                        <MankTextField
                          v-model="dispatchForm.agentName"
                          dense
                          hide-details="auto"
                          placeholder="base-agent"/>
                      </div>

                      <div class="form-actions">
                        <v-btn
                          color="primary"
                          class="mank-btn"
                          elevation="0"
                          :loading="savingDispatch"
                          :disabled="!canSubmitDispatch || savingDispatch"
                          @click="submitDispatch">
                          <Icon name="plus" variant="solid" size="20" color="onPrimary"/>
                          Create dispatch rule
                        </v-btn>
                      </div>
                    </div>
                  </template>
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>
        </template>
      </div>

      <DeleteConfirmationModal
        :visible="showDeleteConfirm"
        :confirm-loading="deleteLoading"
        :detail="deleteModalDetail"
        @cancel="cancelDelete"
        @confirm="confirmDelete"/>

      <SipTrunkDetailDrawer
        :visible="detailDrawerOpen"
        :kind="detailDrawerKind"
        :resource-id="detailDrawerResourceId"
        @close="closeDetailDrawer"
        @saved="onDetailSaved"/>
    </div>
  </div>
</template>

<script>
import DeleteConfirmationModal from '@/components/DeleteConfirmationModal.vue';
import SipTrunkDetailDrawer from '@/components/SipTrunkDetailDrawer.vue';
import Icon from '@/components/Icon.vue';
import MankTextField from '@/components/MankTextField.vue';
import MankTextarea from '@/components/MankTextarea.vue';
import LivekitService from '@/services/LivekitService';

export default {
  name: 'SipTrunk',
  components: {
    DeleteConfirmationModal,
    SipTrunkDetailDrawer,
    Icon,
    MankTextField,
    MankTextarea,
  },
  data() {
    return {
      loadingInitial: true,
      savingTrunk: false,
      savingDispatch: false,
      showDeleteConfirm: false,
      deleteLoading: false,
      pendingDelete: null,
      trunks: [],
      dispatchRules: [],
      trunkForm: { name: '', auth_username: '', auth_password: '', numbersRaw: '', allowedAddressesRaw: '', metadata: '' },
      dispatchForm: { ruleName: '', roomName: '', agentName: 'base-agent' },
      detailDrawerOpen: false,
      detailDrawerKind: 'trunk',
      detailDrawerResourceId: '',
      trunkTableHeaders: [
        { text: 'SIP trunk id', value: 'sipTrunkId', sortable: false },
        { text: 'Name', value: 'name', sortable: false },
        { text: 'Username', value: 'username', sortable: false },
        { text: 'Numbers', value: 'numbers', sortable: false },
        { text: '', value: 'actions', align: 'end', sortable: false, width: '118px' },
      ],
      dispatchTableHeaders: [
        { text: 'Rule id', value: 'ruleId', sortable: false },
        { text: 'Name', value: 'name', sortable: false },
        { text: '', value: 'actions', align: 'end', sortable: false, width: '118px' },
      ],
    };
  },
  computed: {
    hasTrunks() {
      return this.trunks.length > 0;
    },
    hasDispatchRules() {
      return this.dispatchRules.length > 0;
    },
    deleteModalDetail() {
      const p = this.pendingDelete;
      if (!p) return '';
      if (p.kind === 'trunk') return `Inbound trunk ${p.id} will be removed. This cannot be undone.`;
      return `Dispatch rule ${p.id} will be removed. This cannot be undone.`;
    },
    canCreateDispatch() {
      return this.hasTrunks && !this.hasDispatchRules;
    },
    trunkTableRows() {
      return this.trunks.map((t, i) => {
        const trunkId = this.pickTrunkId(t);
        return {
          sipTrunkId: trunkId || '—',
          deleteTrunkId: trunkId,
          name: this.pickTrunkField(t, 'name') || '—',
          username: this.pickTrunkField(t, 'auth_username') || '—',
          numbers: this.formatListField(this.pickTrunkNumbers(t)),
          key: `t-${i}`,
        };
      });
    },
    dispatchTableRows() {
      return this.dispatchRules.map((r, i) => {
        const ruleId = this.pickRuleId(r);
        return {
          ruleId: ruleId || '—',
          deleteRuleId: ruleId,
          name: r.name || (r.rule && r.rule.name) || r.metadata || '—',
          key: `d-${i}`,
        };
      });
    },
    canSubmitTrunk() {
      if (this.hasTrunks) return false;
      return Boolean(
        this.trunkForm.name?.trim()
        && this.trunkForm.auth_username?.trim()
        && this.trunkForm.auth_password?.trim(),
      );
    },
    canSubmitDispatch() {
      if (!this.canCreateDispatch || this.hasDispatchRules) return false;
      return Boolean(this.dispatchForm.roomName?.trim());
    },
  },
  async mounted() {
    await this.reload();
  },
  methods: {
    parseLines(raw) {
      if (!raw || !String(raw).trim()) return [];
      return String(raw)
        .split(/[\n,]+/)
        .map((s) => s.trim())
        .filter(Boolean);
    },
    pickTrunkId(entity) {
      if (!entity) return '';
      if (typeof entity === 'string') return entity;
      if (typeof entity !== 'object') return '';
      const inner = entity.trunk;
      const candidates = [
        entity.sip_trunk_id,
        entity.sipTrunkId,
        entity.SipTrunkId,
        entity.trunk_id,
        entity.trunkId,
        entity.id,
        inner && inner.sip_trunk_id,
        inner && inner.sipTrunkId,
        inner && inner.SipTrunkId,
        inner && inner.trunk_id,
        inner && inner.id,
      ];
      for (let i = 0; i < candidates.length; i += 1) {
        const c = candidates[i];
        if (typeof c === 'string' && c.length) return c;
      }
      const collectStId = (obj) => {
        if (!obj || typeof obj !== 'object') return '';
        const vals = Object.values(obj);
        for (let j = 0; j < vals.length; j += 1) {
          const v = vals[j];
          if (typeof v === 'string' && /^ST_[A-Za-z0-9]+$/.test(v)) return v;
        }
        return '';
      };
      let found = collectStId(entity);
      if (found) return found;
      if (inner && typeof inner === 'object') found = collectStId(inner);
      return found || '';
    },
    pickTrunkField(entity, field) {
      if (!entity) return '';
      return entity[field] ?? entity.trunk?.[field] ?? '';
    },
    pickTrunkNumbers(entity) {
      if (!entity) return [];
      const nums = entity.numbers ?? entity.trunk?.numbers;
      return Array.isArray(nums) ? nums : [];
    },
    pickRuleId(entity) {
      if (!entity) return '';
      return (
        entity.sip_dispatch_rule_id
        || entity.dispatch_rule_id
        || entity.rule_id
        || entity.id
        || ''
      );
    },
    formatListField(arr) {
      if (!arr || arr.length === 0) return '—';
      return arr.join(', ');
    },
    async reload() {
      this.loadingInitial = true;
      try {
        const [listTrunks, listRules] = await Promise.all([
          LivekitService.listInboundTrunks(),
          LivekitService.listDispatchRules(),
        ]);
        this.trunks = Array.isArray(listTrunks) ? listTrunks : [];
        this.dispatchRules = Array.isArray(listRules) ? listRules : [];
      } catch (err) {
        if (err.response?.status !== 401) {
          this.$store.dispatch('ui/showNotification', {
            type: 'error',
            message: err.response?.data?.message || 'Could not load SIP configuration.',
            timeout: 5000,
          });
        }
      } finally {
        this.loadingInitial = false;
      }
    },
    async submitTrunk() {
      if (!this.canSubmitTrunk) return;
      const trunk = {
        name: this.trunkForm.name.trim(),
        auth_username: this.trunkForm.auth_username.trim(),
        auth_password: this.trunkForm.auth_password,
      };
      const numbers = this.parseLines(this.trunkForm.numbersRaw);
      const allowed_addresses = this.parseLines(this.trunkForm.allowedAddressesRaw);
      if (numbers.length) trunk.numbers = numbers;
      if (allowed_addresses.length) trunk.allowed_addresses = allowed_addresses;
      const meta = this.trunkForm.metadata?.trim();
      if (meta) trunk.metadata = meta;

      this.savingTrunk = true;
      try {
        await LivekitService.createInboundTrunk({ trunk });
        this.$store.dispatch('ui/showNotification', {
          type: 'success',
          message: 'Inbound trunk created.',
          timeout: 5000,
        });
        Object.assign(this.trunkForm, {
          name: '',
          auth_username: '',
          auth_password: '',
          numbersRaw: '',
          allowedAddressesRaw: '',
          metadata: '',
        });
        await this.reload();
      } catch (err) {
        if (!err.response || err.response.status !== 401) {
          this.$store.dispatch('ui/showNotification', {
            type: 'error',
            message: err.response?.data?.message || 'Could not create trunk.',
            timeout: 5000,
          });
        }
      } finally {
        this.savingTrunk = false;
      }
    },
    async submitDispatch() {
      if (!this.canSubmitDispatch || !this.canCreateDispatch) return;
      const firstId = this.pickTrunkId(this.trunks[0]);
      if (!firstId) {
        this.$store.dispatch('ui/showNotification', {
          type: 'error',
          message: 'Could not read trunk id from listing. Refresh and try again.',
          timeout: 5000,
        });
        return;
      }

      const agentName = (this.dispatchForm.agentName || 'base-agent').trim() || 'base-agent';
      const body = {
        trunk_ids: [firstId],
        rule: { dispatch_rule_direct: { room_name: this.dispatchForm.roomName.trim() } },
        room_config: { agents: [{ agent_name: agentName }] },
      };
      const rn = this.dispatchForm.ruleName?.trim();
      if (rn) body.name = rn;

      this.savingDispatch = true;
      try {
        await LivekitService.createDispatchRule(body);
        this.$store.dispatch('ui/showNotification', {
          type: 'success',
          message: 'Dispatch rule created.',
          timeout: 5000,
        });
        Object.assign(this.dispatchForm, {
          ruleName: '',
          roomName: '',
          agentName: 'base-agent',
        });
        await this.reload();
      } catch (err) {
        if (!err.response || err.response.status !== 401) {
          this.$store.dispatch('ui/showNotification', {
            type: 'error',
            message: err.response?.data?.message || 'Could not create dispatch rule.',
            timeout: 5000,
          });
        }
      } finally {
        this.savingDispatch = false;
      }
    },
    openDeleteTrunk(item) {
      if (!item.deleteTrunkId || this.deleteLoading) return;
      this.pendingDelete = { kind: 'trunk', id: item.deleteTrunkId };
      this.showDeleteConfirm = true;
    },
    openDeleteDispatch(item) {
      if (!item.deleteRuleId || this.deleteLoading) return;
      this.pendingDelete = { kind: 'dispatch', id: item.deleteRuleId };
      this.showDeleteConfirm = true;
    },
    cancelDelete() {
      if (this.deleteLoading) return;
      this.showDeleteConfirm = false;
      this.pendingDelete = null;
    },
    async confirmDelete() {
      if (!this.pendingDelete || this.deleteLoading) return;
      const { kind, id } = this.pendingDelete;
      this.deleteLoading = true;
      try {
        if (kind === 'trunk') await LivekitService.deleteInboundTrunk(id);
        else await LivekitService.deleteDispatchRule(id);

        const message = kind === 'trunk' ? 'Inbound trunk deleted.' : 'Dispatch rule deleted.';
        this.$store.dispatch('ui/showNotification', {
          type: 'success',
          message,
          timeout: 5000,
        });
        this.showDeleteConfirm = false;
        this.pendingDelete = null;
        await this.reload();
      } catch (err) {
        if (!err.response || err.response.status !== 401) {
          const fallback = kind === 'trunk'
            ? 'Could not delete trunk. Remove dependent dispatch rules first if the API rejects the request.'
            : 'Could not delete dispatch rule.';
          this.$store.dispatch('ui/showNotification', {
            type: 'error',
            message: err.response?.data?.message || fallback,
            timeout: 5000,
          });
        }
      } finally {
        this.deleteLoading = false;
      }
    },
    openDetailDrawer(kind, resourceId) {
      if (!resourceId || this.deleteLoading) return;
      this.detailDrawerKind = kind === 'dispatch' ? 'dispatch' : 'trunk';
      this.detailDrawerResourceId = resourceId;
      this.detailDrawerOpen = true;
    },
    closeDetailDrawer() {
      this.detailDrawerOpen = false;
      this.detailDrawerResourceId = '';
      this.detailDrawerKind = 'trunk';
    },
    onDetailSaved() {
      this.reload();
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@/styles/colors.scss';
@import '@/styles/variables.scss';

.sip-trunk-page {
  height: 100%;
}

.page-subtitle {
  font-family: $body-font-family;
  font-size: $body-3-size;
  color: $light-foreground-3;
  margin: 8px 0 0;
}

.main-content {
  padding: 24px;
}

.sip-section-row {
  margin-top: 0;
}

.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
  padding: 48px;
}

.loading-text {
  font-family: $body-font-family;
  font-size: $body-3-size;
  color: $light-foreground-4;
  margin: 0;
}

.section-card.sip-panel-card {
  border-radius: 12px;
  border: 1px solid $light-border;
  height: 100%;
  overflow: hidden;
}

.sip-panel-header {
  padding: 20px $mank-space-6 0;
}

.sip-panel-title {
  font-family: $body-font-family;
  font-weight: 700;
  font-size: $h5-size;
  line-height: 1.35;
  color: $light-foreground-1;
  margin: 0 0 8px;
}

.sip-panel-description {
  margin: 0;
}

.sip-panel-body {
  padding-top: 12px !important;
  padding-bottom: 20px !important;
}

.sip-empty-copy {
  line-height: 1.5;
}

.sip-divider {
  border-color: $light-border;
  margin: 16px 0 20px !important;
}

.sip-form-heading {
  margin: 0 0 6px;
}

.sip-form-intro {
  margin: 0 0 20px;
}

.sip-form-block {
  padding-bottom: 4px;
}

.sip-form-grid {
  margin-bottom: 0;
}

.form-group {
  margin-bottom: 18px;
}

.input-label {
  display: block;
  font-size: $text-size-13;
  font-weight: 600;
  color: $light-foreground-1;
  margin-bottom: 8px;
}

.required {
  color: $light-primary;
}

.form-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-top: 8px;
  padding-top: 20px;
  border-top: 1px solid $light-border;
}

.sip-banner {
  border-radius: 8px;
  font-family: $body-font-family;
}

.sip-table-wrap {
  border-radius: 8px;
  border: 1px solid $light-border;
  overflow: hidden;
  background: $light-surface-1;
}

.sip-row-actions {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  min-height: 32px;

  &.sip-row-actions--split {
    display: inline-flex;
    gap: 2px;
  }
}

.sip-data-table {

  ::v-deep .v-data-table-header {
    background-color: $light-surface-1;
  }

  ::v-deep .v-data-table-header tr th {
    color: $light-foreground-3 !important;
    font-weight: 600;
    font-size: $body-3-size !important;
    text-transform: capitalize;
  }

  ::v-deep .v-data-table__wrapper {
    overflow-x: auto;
  }

  ::v-deep .v-data-table__tr:hover {
    background-color: rgba($light-surface-2, 0.6);
  }
}
</style>
